import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { AlertDialog as AlertDialogPrimitive, Dialog as DialogPrimitive } from 'radix-ui';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { cn, focusRing } from '../lib/cn.js';
import { buttonVariants } from './button.js';

const overlay =
  'fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out';
/*
 * The panel never outgrows the viewport (review UI3A-201): at most the screen less 1 rem on
 * each side. Header and footer stay put and only `DialogBody` scrolls, so the primary action
 * is always on screen. On a very short viewport (200 % zoom, a phone on its side: `short:`)
 * the whole panel scrolls instead and the footer sticks to its bottom edge.
 */
const panel =
  'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto overscroll-contain rounded-xl border border-border bg-surface-raised p-6 text-fg shadow-xl data-[state=open]:animate-pop-in';

// ---------------------------------------------------------------- Dialog

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogContent({
  className,
  children,
  hideClose = false,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & { hideClose?: boolean }) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content className={cn(panel, className)} {...props}>
        {children}
        {!hideClose && (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-3 top-3 inline-flex size-touch items-center justify-center rounded-md text-muted hover:bg-surface-hover hover:text-fg',
              focusRing,
            )}
            aria-label="Close"
          >
            <X aria-hidden className="size-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex shrink-0 flex-col gap-1.5 pr-8', className)} {...props} />;
}

/** The actions. Never scrolls away: below the body, or stuck to the bottom when short. */
export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        'short:sticky short:bottom-0 short:z-10 short:-mx-6 short:-mb-6 short:border-t short:border-border short:bg-surface-raised short:px-6 short:pb-6 short:pt-4',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The part of a dialog that scrolls when it is taller than the screen (UI3A-201). Put
 * everything between `DialogHeader` and `DialogFooter` in it; `className` lays out its
 * content (a column with `gap-5` by default). While it scrolls it shows a hairline above and
 * below, and it can be scrolled from the keyboard.
 */
export function DialogBody({ className, children, ...props }: ComponentProps<'div'>) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const box = outer.current;
    const content = inner.current;
    if (!box || !content || typeof ResizeObserver === 'undefined') return;
    // Fires once on observe, then whenever the box or its content changes size.
    const watch = new ResizeObserver(() => setScrolls(box.scrollHeight > box.clientHeight + 1));
    watch.observe(box);
    watch.observe(content);
    return () => watch.disconnect();
  }, []);
  return (
    <div
      ref={outer}
      data-scrolls={scrolls || undefined}
      // Focusable only while it scrolls, so the arrow keys reach it (and first focus still
      // lands on the first field).
      tabIndex={scrolls ? 0 : undefined}
      className={cn(
        '-mx-6 -my-1 min-h-0 flex-auto overflow-y-auto overscroll-contain px-6 py-1 outline-none',
        'data-[scrolls]:border-y data-[scrolls]:border-border data-[scrolls]:py-3',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid focus-visible:outline-focus-ring',
        'short:flex-none short:overflow-visible short:data-[scrolls]:border-0',
      )}
      {...props}
    >
      <div ref={inner} className={cn('flex flex-col gap-5', className)}>
        {children}
      </div>
    </div>
  );
}

/**
 * A dialog's form: header, `DialogBody` and footer inside it keep their places (UI3A-201).
 * `noValidate` by default: fields are checked in words by the form itself.
 */
export function DialogForm({ className, ...props }: ComponentProps<'form'>) {
  return (
    <form
      noValidate
      className={cn('flex min-h-0 flex-col gap-4 short:flex-none', className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('font-display text-lg font-semibold leading-tight', className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description className={cn('text-body text-muted', className)} {...props} />
  );
}

// ---------------------------------------------------------------- AlertDialog

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

export function AlertDialogContent({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className={overlay} />
      <AlertDialogPrimitive.Content className={cn(panel, 'max-w-md', className)} {...props} />
    </AlertDialogPrimitive.Portal>
  );
}

export const AlertDialogHeader = DialogHeader;
/** An alert dialog's body: the part that scrolls, so its actions stay on screen (B-041). */
export const AlertDialogBody = DialogBody;
export const AlertDialogFooter = DialogFooter;

export function AlertDialogTitle({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      className={cn('font-display text-lg font-semibold leading-tight', className)}
      {...props}
    />
  );
}

export function AlertDialogDescription({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      className={cn('text-body text-muted', className)}
      {...props}
    />
  );
}

export function AlertDialogAction({
  className,
  destructive = false,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Action> & { destructive?: boolean }) {
  return (
    <AlertDialogPrimitive.Action
      className={cn(
        buttonVariants({ variant: destructive ? 'destructiveSolid' : 'primary' }),
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogCancel({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant: 'secondary' }), className)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------- Sheet (side panel)

const sheetVariants = cva(
  'fixed z-50 flex flex-col gap-4 bg-surface-raised text-fg shadow-xl outline-none',
  {
    variants: {
      side: {
        right:
          'inset-y-0 right-0 h-full w-[min(28rem,100%)] border-l border-border data-[state=open]:animate-slide-in-right',
        left: 'inset-y-0 left-0 h-full w-[min(20rem,100%)] border-r border-border data-[state=open]:animate-slide-in-left',
      },
    },
    defaultVariants: { side: 'right' },
  },
);

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

export function SheetContent({
  className,
  children,
  side,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & VariantProps<typeof sheetVariants>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content className={cn(sheetVariants({ side }), className)} {...props}>
        {children}
        <DialogPrimitive.Close
          className={cn(
            'absolute right-3 top-3 inline-flex size-touch items-center justify-center rounded-md text-muted hover:bg-surface-hover hover:text-fg',
            focusRing,
          )}
          aria-label="Close"
        >
          <X aria-hidden className="size-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1.5 p-6 pb-0 pr-14', className)} {...props} />;
}

export const SheetTitle = DialogTitle;
export const SheetDescription = DialogDescription;
